import { ChatTranscript } from '@/app/users/chats/ChatTranscript';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { conversationState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import { ChatConversationIdSchema, getConversationLabel, isConversationReadOnly, type UserId } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

export const ChatConversationPage: FC<{ userId: UserId }> = ({ userId }) => {
    const navigate = useNavigate();
    const { conversationId: rawConversationId } = useParams<{ conversationId: string }>();
    const conversationId = rawConversationId ? ChatConversationIdSchema.parse(rawConversationId) : undefined;
    const [conversation, conversationLoadable] = useLoadableAtomValue(
        conversationState(conversationId ? { userId, conversationId } : undefined),
    );

    if (!conversation) {
        return (
            <div className="text-muted-foreground">
                {conversationLoadable.state === 'loading' ? 'Loading conversation…' : 'Conversation not found.'}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" aria-label="Back to conversations" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex min-w-0 flex-col">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-xl font-semibold">{getConversationLabel(conversation)}</h2>
                        {isConversationReadOnly(conversation) && <Badge variant="outline">Read-only</Badge>}
                    </div>
                    <IdWithCopy id={conversation.id} variant="muted" />
                </div>
            </div>

            <Card>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                        <MetadataItem label="Started" value={formatDateTime(conversation.createdAt)} />
                        <MetadataItem label="Last activity" value={formatDateTime(conversation.updatedAt)} />
                        <MetadataItem
                            label="Tokens in / out"
                            value={`${conversation.tokensIn.toLocaleString()} / ${conversation.tokensOut.toLocaleString()}`}
                        />
                        <MetadataItem label="Est. cost" value={`$${conversation.estimatedCostUsd.toFixed(3)}`} />
                        <MetadataItem
                            label="Home"
                            value={
                                <Link
                                    className="text-info hover:underline"
                                    to={`/directory/homes/${conversation.homeId}`}
                                >
                                    {conversation.homeId}
                                </Link>
                            }
                        />
                        <MetadataItem
                            label="Intent"
                            value={
                                conversation.intent ? (
                                    <Badge variant="secondary">{conversation.intent.kind}</Badge>
                                ) : (
                                    '—'
                                )
                            }
                        />
                        <MetadataItem
                            label="Tools loaded"
                            value={
                                conversation.loadedToolNames.length > 0 ? conversation.loadedToolNames.join(', ') : '—'
                            }
                        />
                        <MetadataItem label="Messages" value={conversation.messages.length} />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Transcript</CardTitle>
                </CardHeader>
                <CardContent>
                    <ChatTranscript conversation={conversation} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Object</CardTitle>
                </CardHeader>
                <CardContent>
                    <JsonViewer data={conversation} />
                </CardContent>
            </Card>
        </div>
    );
};
